import { AccountTypes } from '@interfaces/models';
import { FlowTypes } from '@shared/models';

import { HydrationResult, HydrationTriage } from '../hydration.models';
import type { MinorDraft } from '../ku4-draft.models';
import { hydrateAccountDetails } from '../mappers/account-details.mapper';
import { buildDocumentFileBuckets } from '../mappers/document-files.mapper';
import { hydratePrimaryOwner } from '../mappers/primary-owner.mapper';

/**
 * Only what the minor route guards read. `accountTypeInheriting` is required by
 * InitStateGuard — MINOR is not in its exemption list, so every secure minor route would
 * reject a resumed draft without it. Inheritance-only derivations are deliberately not set,
 * so a resumed minor draft cannot pick up inheritance-flavoured routing.
 */
function buildMinorTriage(): HydrationTriage {
  return {
    flowType: FlowTypes.MINOR,
    accountTypeInheriting: AccountTypes.UGMA_UTMA,
    // Resume skips triage-summary, the only place that normally sets this, and a draft does
    // not persist triage — without it `secure/submit` 400s on the initial empty string.
    // Invariant for UGMA/UTMA: the former minor always acts as themselves.
    //
    // The literal 'YES', not YesNoEmpty.YES ('Yes') — the triage slice stores upper-case
    // and triage.guard.ts checks `isClient === 'YES'`.
    isClient: 'YES',
  };
}

/**
 * Read path for minor (UGMA_UTMA) drafts. Deliberately absent: `positions` (a minor transfer
 * moves whole accounts, so the write path sends none). Of the document buckets only the letter
 * applies — the minor flow has no document or tax upload page — but the shared split still runs
 * over every category so a non-letter entry can never leak into the letter slice.
 */
export function hydrateMinor(draft: MinorDraft): HydrationResult {
  const result: HydrationResult = {
    triage: buildMinorTriage(),
  };

  if (draft.experienceType !== undefined) {
    result.experienceType = draft.experienceType;
  }

  const accountDetails = hydrateAccountDetails(draft.accountDetails);

  if (accountDetails.selectedAccs) {
    result.selectedAccs = accountDetails.selectedAccs;
  }

  if (accountDetails.settlementFund) {
    result.settlementFund = accountDetails.settlementFund;
  }

  if (accountDetails.investmentObjectives) {
    result.investmentObjectives = accountDetails.investmentObjectives;
  }

  const primaryOwner = hydratePrimaryOwner(draft.primaryOwner);

  if (primaryOwner) {
    result.primaryOwner = primaryOwner;
  }

  // Only letterFiles is expected for minor, but assigning every bucket keeps the category
  // split single-sourced — a DOCUMENT/TAX entry lands in its own slice, never the letter one.
  const documentFileBuckets = buildDocumentFileBuckets(draft.documentInformation?.uploadedFiles);
  Object.assign(result, documentFileBuckets);

  // buildGlobalActions already dispatches updateVgHasLetter when this is set, and the minor
  // resume path runs buildGlobalActions — so restoring the flag here is enough, no new dispatch.
  if (draft.documentInformation?.vanguardHasLetter !== undefined) {
    result.vanguardHasLetter = draft.documentInformation.vanguardHasLetter;
  }

  return result;
}
